import { Component, AfterViewInit, ViewChild, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Subscription, asyncScheduler } from 'rxjs';
import { DfMonthpickerService } from '../services/monthpicker.service';
import { IState } from '../models/component-state.model';
import { YearDataSource } from '../models/year-collection.model';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DataSource } from '@angular/cdk/collections';
import { map, debounceTime, distinctUntilChanged, filter, subscribeOn } from 'rxjs/operators';
import { Value } from '../models/abstract-value-accessor.model';

@Component({
  standalone: false,
  selector: 'df-monthpicker-mobile-view',
  templateUrl: './mobile-view.component.html'
})
export class MonthPickerMobileViewComponent implements OnInit, AfterViewInit, OnDestroy {
  state = <IState>{};
  private subscriptions: Subscription;
  years: DataSource<number>;
  @ViewChild('input', { static: false })
  private input: ElementRef;
  @ViewChild('viewportContainer', { static: false })
  private viewportContainer: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  private scrollViewport: CdkVirtualScrollViewport;
  pageSize: number;
  itemSize: number;
  minBufferPx: number;
  maxBufferPx: number;
  showScrollViewport = false;
  selectedMonth: number;
  getToday :Value;
  // used to skip self-caused updates to state
  private isLockedForUpdate = false;

  constructor(private componentService: DfMonthpickerService) {
    this.subscriptions = new Subscription();
    this.getToday = this.componentService.getToday();
  }

  ngOnInit() {
    this.initState();
    this.initSelectedUpdate();
  }

  private initState() {
    this.subscriptions.add(
      this.componentService
        .getState()
        .pipe(subscribeOn(asyncScheduler))
        .subscribe(this.stateChangeHandler.bind(this))
    );
  }

  private initSelectedUpdate() {
    this.subscriptions.add(
      this.componentService
        .getState()
        .pipe(
          map(state => state.selected.year),
          filter(year => {
            if (this.isLockedForUpdate) {
              this.isLockedForUpdate = false;
              return false;
            }
            return year && !!this.years;
          }),
          distinctUntilChanged(),
          debounceTime(300)
        )
        .subscribe(year => {
          this.setScrollStartYear(year - 1);
          this.resetScroll();
        })
    );
  }

  private stateChangeHandler(state: IState) {
    let valueString = this.componentService.formatValue(state.selected);
    if (state.selected.month < 10) {
      valueString = this.componentService.padMonthWithZero(valueString);
    }
    this.input.nativeElement.value = valueString;
    this.state = state;
  }

  // Single click handler for scroll viewport
  onClick(event: MouseEvent) {
    let element = event.target as HTMLElement;
    if (element.tagName === 'SPAN') {
      element = element.parentElement;
    }
    if (!element.classList.contains('monthpicker__month')) {
      return;
    }
    event.stopPropagation();
    this.isLockedForUpdate = true;
    this.componentService.update({
      selected: {
        year: +element.parentElement.getAttribute('data-year'),
        month: +element.getAttribute('data-month')
      }
    });
  }

  close() {
    this.componentService.update({ opened: false });
  }

  reset() {
    this.componentService.update({ opened: false });
  }

  done() {
    this.componentService.update({ opened: false, value: this.state.selected });
  }

  inputChange() {
    const value = this.componentService.formatValueString(this.input.nativeElement.value);
    this.componentService.update({ selected: value });
  }

  trackByYear = (index: number, item: number) =>
    item.toString() + (this.state.selected.year === item) + (this.getToday.year === item);

  private resetScroll() {
    requestAnimationFrame(() => this.scrollViewport?.scrollToIndex(1));
  }

  private setScrollStartYear(year: number) {
    this.years = new YearDataSource(year, this.pageSize);
  }

  private initScrollViewport() {
    const viewportHeight = this.viewportContainer.nativeElement.clientHeight;
    this.pageSize = Math.floor(viewportHeight / 160) || 1;
    this.itemSize = viewportHeight > 160 ? viewportHeight / this.pageSize : 160;
    this.minBufferPx = Math.floor(this.itemSize / 2);
    this.maxBufferPx = Math.floor(this.itemSize * 2);
    this.showScrollViewport = true;
    this.setScrollStartYear(this.state.selected.year);
    this.resetScroll();
  }

  ngAfterViewInit() {
    // update viewport parameters after change detection and view rendering
    setTimeout(() => this.initScrollViewport());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
